<template>
  <div class="p-4">
    <div
      v-if="!loading && patientsAtRisk > 0"
      class="mb-4 px-4 py-3 rounded border border-amber-200 bg-amber-50 text-xs text-amber-900"
    >
      <strong>{{ fmt(patientsAtRisk) }}</strong> currently attributed and assigned patients sit on a
      provider combination the app cannot fully resolve.
    </div>


    <div class="flex flex-wrap items-center gap-2 mb-4">
      <button
        v-for="f in findingChips"
        :key="f.key"
        class="px-3 py-1.5 rounded border text-xs"
        :class="activeFinding === f.key
          ? 'bg-blue-600 text-white border-blue-600'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="activeFinding = f.key"
      >
        {{ f.label }}
        <span class="ml-1 opacity-70">{{ f.count }}</span>
      </button>

      <div class="flex-1"></div>

      <input
        v-model="search"
        type="text"
        placeholder="Filter by NPI, TIN, name, payer, or contract"
        class="border border-gray-300 rounded px-3 py-1.5 text-xs w-64"
      />
      <button
        class="px-3 py-1.5 border border-gray-300 rounded text-xs hover:bg-gray-50"
        :disabled="!filteredRows.length"
        @click="exportCsv"
      >Export CSV</button>
    </div>

    <LoadingIndicator v-if="loading" />

    <div v-else-if="error" class="text-red-600 text-xs p-4 border border-red-200 rounded bg-red-50">
      {{ error }}
    </div>

    <div v-else class="border border-gray-200 rounded max-h-[70vh] overflow-y-auto">
      <table class="w-full table-fixed text-xs">
        <colgroup>
          <col style="width:11%" />
          <col style="width:15%" />
          <col style="width:10%" />
          <col style="width:9%" />
          <col style="width:7%" />
          <col style="width:7%" />
          <col style="width:7%" />
          <col style="width:14%" />
          <col style="width:10%" />
          <col style="width:10%" />
        </colgroup>
        <thead class="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
          <tr>
            <th class="text-left px-2 py-1.5 font-medium">Payer</th>
            <th class="text-left px-2 py-1.5 font-medium">Contract</th>
            <th class="text-left px-2 py-1.5 font-medium">NPI</th>
            <th class="text-left px-2 py-1.5 font-medium">TIN</th>
            <th class="text-left px-2 py-1.5 font-medium">CCN</th>
            <th class="text-right px-2 py-1.5 font-medium">Attr+Asgn</th>
            <th class="text-right px-2 py-1.5 font-medium">Assigned</th>
            <th class="text-left px-2 py-1.5 font-medium">App staff</th>
            <th class="text-left px-2 py-1.5 font-medium">App TIN</th>
            <th class="text-left px-2 py-1.5 font-medium">Finding</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(r, idx) in filteredRows" :key="idx" class="border-b border-gray-100 hover:bg-gray-50">
            <td class="px-2 py-1.5 truncate" :title="r.PAYER">{{ r.PAYER || '—' }}</td>
            <td class="px-2 py-1.5 truncate" :title="r.CONTRACT_ID">{{ r.CONTRACT_ID || '—' }}</td>
            <td class="px-2 py-1.5 truncate">{{ r.NPI || '—' }}</td>
            <td class="px-2 py-1.5 truncate">{{ r.TIN || '—' }}</td>
            <td class="px-2 py-1.5 truncate">{{ r.CCN || '—' }}</td>
            <td class="px-2 py-1.5 text-right font-medium">{{ fmt(r.PATIENTS_ATTRIBUTED_AND_ASSIGNED) }}</td>
            <td class="px-2 py-1.5 text-right text-gray-600">{{ fmt(r.PATIENTS_ASSIGNED) }}</td>
            <td class="px-2 py-1.5 truncate" :title="r.APP_STAFF_NAME">
              <router-link
                v-if="r.APP_STAFF_ENTITY_ID"
                :to="`/entity/${r.APP_STAFF_ENTITY_ID}`"
                class="text-blue-600 hover:underline"
              >{{ r.APP_STAFF_NAME }}</router-link>
              <span v-else class="text-red-600">not in app</span>
            </td>
            <td class="px-2 py-1.5 truncate" :title="r.APP_TIN_NAME">
              <router-link
                v-if="r.APP_TIN_ENTITY_ID"
                :to="`/entity/${r.APP_TIN_ENTITY_ID}`"
                class="text-blue-600 hover:underline"
              >{{ r.APP_TIN_NAME }}</router-link>
              <span v-else-if="r.TIN" class="text-red-600">not in app</span>
              <span v-else>—</span>
            </td>
            <td class="px-2 py-1.5 truncate" :title="label(r.FINDING)">
              <span class="px-1.5 py-0.5 rounded" :class="badgeClass(r.FINDING)">
                {{ shortLabel(r.FINDING) }}
              </span>
            </td>
          </tr>
          <tr v-if="!filteredRows.length">
            <td colspan="10" class="px-3 py-8 text-center text-gray-500">
              No rows match the current filter.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

const FINDING_LABELS = {
  ALL: 'All',
  PROBLEMS: 'Problems only',
  NEITHER_NPI_NOR_TIN_IN_APP: 'Neither NPI nor TIN in app',
  NPI_MISSING_FROM_APP: 'NPI missing from app',
  TIN_MISSING_FROM_APP: 'TIN missing from app',
  CCN_MISSING_FROM_APP: 'CCN missing from app',
  RELATIONSHIP_MISSING_FROM_APP: 'Both exist — relationship missing',
  OK: 'OK',
};

// The badge column is narrow; full sentences are shown on hover via title.
const SHORT_LABELS = {
  NEITHER_NPI_NOR_TIN_IN_APP: 'No NPI/TIN',
  NPI_MISSING_FROM_APP: 'No NPI',
  TIN_MISSING_FROM_APP: 'No TIN',
  CCN_MISSING_FROM_APP: 'No CCN',
  RELATIONSHIP_MISSING_FROM_APP: 'No link',
  OK: 'OK',
};

export default {
  name: 'ReconciliationAssignment',
  components: { LoadingIndicator },
  // The NPI/TIN/name filter identifies real providers and practices. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  data() {
    return {
      loading: true,
      error: null,
      rows: [],
      summary: {},
      patientsAtRisk: 0,
      activeFinding: 'PROBLEMS',
      search: '',
    };
  },
  computed: {
    findingChips() {
      const total = this.rows.length;
      const problems = this.rows.filter((r) => r.FINDING !== 'OK').length;
      const chips = [
        { key: 'ALL', label: FINDING_LABELS.ALL, count: total },
        { key: 'PROBLEMS', label: FINDING_LABELS.PROBLEMS, count: problems },
      ];
      Object.keys(this.summary)
        .sort((a, b) => this.summary[b] - this.summary[a])
        .forEach((k) => chips.push({
          key: k, label: FINDING_LABELS[k] || k, count: this.summary[k],
        }));
      return chips;
    },
    filteredRows() {
      let out = this.rows;
      if (this.activeFinding === 'PROBLEMS') {
        out = out.filter((r) => r.FINDING !== 'OK');
      } else if (this.activeFinding !== 'ALL') {
        out = out.filter((r) => r.FINDING === this.activeFinding);
      }
      const q = this.search.trim().toLowerCase();
      if (q) {
        out = out.filter((r) => (
          (r.NPI || '').includes(q)
          || (r.TIN || '').includes(q)
          || (r.CCN || '').toLowerCase().includes(q)
          || (r.APP_STAFF_NAME || '').toLowerCase().includes(q)
          || (r.APP_TIN_NAME || '').toLowerCase().includes(q)
          || (r.PAYER || '').toLowerCase().includes(q)
          || (r.CONTRACT_ID || '').toLowerCase().includes(q)
        ));
      }
      return out;
    },
  },
  mounted() { this.fetch(); },
  methods: {
    async fetch() {
      this.loading = true;
      this.error = null;
      try {
        const res = await axios.get('/api/entities/empi-coverage/');
        this.rows = res.data.rows || [];
        this.summary = res.data.summary || {};
        this.patientsAtRisk = res.data.patients_at_risk || 0;
      } catch (e) {
        this.error = e.response?.data?.detail || 'Could not load EMPI coverage data.';
      } finally {
        this.loading = false;
      }
    },
    label(f) { return FINDING_LABELS[f] || f; },
    shortLabel(f) { return SHORT_LABELS[f] || FINDING_LABELS[f] || f; },
    fmt(n) {
      if (n === null || n === undefined) return '—';
      return Number(n).toLocaleString();
    },
    badgeClass(f) {
      switch (f) {
        case 'OK': return 'bg-green-100 text-green-800';
        case 'RELATIONSHIP_MISSING_FROM_APP': return 'bg-amber-100 text-amber-800';
        default: return 'bg-red-100 text-red-800';
      }
    },
    exportCsv() {
      const cols = ['PAYER', 'CONTRACT_ID', 'NPI', 'TIN', 'CCN',
        'PATIENTS_ATTRIBUTED_AND_ASSIGNED', 'PATIENTS_ASSIGNED',
        'APP_STAFF_ENTITY_ID', 'APP_STAFF_NAME',
        'APP_TIN_ENTITY_ID', 'APP_TIN_NAME', 'FINDING'];
      const esc = (v) => {
        if (v === null || v === undefined) return '';
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [cols.join(',')]
        .concat(this.filteredRows.map((r) => cols.map((c) => esc(r[c])).join(',')))
        .join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'empi_entity_coverage.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
